import { Artist, Content, Series, Track } from '../models/TrackInstallVisibility';

const BYTES_PER_MB = 1024 * 1024;
const BYTES_PER_GB = 1024 * 1024 * 1024;

const toBytes = (value: number | string | undefined): number => {
    const bytes = Number(value);
    return Number.isFinite(bytes) && bytes > 0 ? bytes : 0;
};

// Download size of one track: its volumetric data plus audio, when known
export const trackBytes = (track: Track): number => {
    return toBytes(track.volumeByteSize) + toBytes(track.audioByteSize);
};

// A series is installed as a whole, so its size is the sum of its tracks
export const seriesBytes = (series: Series): number => {
    return (series.tracks ?? []).reduce((total, track) => total + trackBytes(track), 0);
};

// Size of each track that is selected for download, keyed by track id.
// Tracks of a selected series are included.
export const selectedTrackSizes = (content: Content | null | undefined): Map<string, number> => {
    const sizes = new Map<string, number>();
    if (!content || !content.artists) return sizes;
    content.artists.forEach((artist: Artist) => {
        (artist.tracks ?? []).forEach((track) => {
            if (track.install) sizes.set(track.id, trackBytes(track));
        });
        (artist.series ?? []).forEach((series) => {
            if (!series.install) return;
            (series.tracks ?? []).forEach((track) => sizes.set(track.id, trackBytes(track)));
        });
    });
    return sizes;
};

// Total size of everything selected for download
export const selectedContentBytes = (content: Content | null | undefined): number => {
    let total = 0;
    selectedTrackSizes(content).forEach((bytes) => { total += bytes; });
    return total;
};

export const mbToBytes = (mb: number): number => mb * BYTES_PER_MB;

// e.g. "1.8 GB", "640 MB". Uses the same 1024 based units the headset reports.
export const formatBytes = (bytes: number): string => {
    if (!Number.isFinite(bytes) || bytes <= 0) return '0 MB';
    if (bytes >= BYTES_PER_GB) return `${(bytes / BYTES_PER_GB).toFixed(1)} GB`;
    return `${Math.max(1, Math.round(bytes / BYTES_PER_MB))} MB`;
};

// Size of every track in the catalogue, keyed by track id (series tracks included)
export const allTrackSizes = (content: Content | null | undefined): Map<string, number> => {
    const sizes = new Map<string, number>();
    if (!content || !content.artists) return sizes;
    content.artists.forEach((artist: Artist) => {
        (artist.tracks ?? []).forEach((track) => sizes.set(track.id, trackBytes(track)));
        (artist.series ?? []).forEach((series) => {
            (series.tracks ?? []).forEach((track) => sizes.set(track.id, trackBytes(track)));
        });
    });
    return sizes;
};

export interface HeadsetStorageIssue {
    name: string;
    // what the headset would have to store or download for this selection
    neededBytes: number;
    // what it has room for
    availableBytes: number;
}

// Checks a content selection against the storage each headset last reported and
// returns the headsets it does not fit on.
//
// When a headset reports free space, the selection fits if everything it still
// has to download fits in that free space. Content that is selected but not yet
// installed (queued, downloading, awaiting the device) therefore counts as space
// already taken, and installed content that is being deselected counts as space
// that will be given back. Headsets that only report total storage are checked
// against that; headsets that report nothing are not checked.
export const headsetStorageIssues = (
    content: Content | null | undefined,
    devices: Record<string, any> | null | undefined
): HeadsetStorageIssue[] => {
    const issues: HeadsetStorageIssue[] = [];
    const selected = selectedTrackSizes(content);
    const all = allTrackSizes(content);
    Object.values(devices ?? {}).forEach((device: any) => {
        if (!device) return;
        const hasFree = typeof device.freeSpaceMB === 'number' && device.freeSpaceMB >= 0;
        const hasTotal = typeof device.totalSpaceMB === 'number' && device.totalSpaceMB > 0;
        if (!hasFree && !hasTotal) return;

        let neededBytes = 0;
        let availableBytes = 0;
        if (hasFree) {
            const installed = new Set<string>();
            (device.artists ?? []).forEach((artist: any) => (artist.tracks ?? []).forEach((track: any) => {
                if (track && track.installed) installed.add(track.id);
            }));
            availableBytes = mbToBytes(device.freeSpaceMB);
            selected.forEach((bytes, trackId) => {
                if (!installed.has(trackId)) neededBytes += bytes;
            });
            installed.forEach((trackId) => {
                if (!selected.has(trackId)) availableBytes += all.get(trackId) ?? 0;
            });
        } else {
            availableBytes = mbToBytes(device.totalSpaceMB);
            selected.forEach((bytes) => { neededBytes += bytes; });
        }
        if (neededBytes > availableBytes) {
            issues.push({ name: String(device.name ?? 'Headset'), neededBytes, availableBytes });
        }
    });
    return issues;
};
